import {
  ViewMode,
  PaginationOptions,
  ORDER_REQUEST_STATUS,
  ORDER_CHANGE_SHOW_MY_ORDERS,
} from '../constants';
import { get, post } from '../Services/http';
import { catchError } from './Error.action';

import {
  ORDER_RECEIVE,
  ORDER_ERROR,
  ORDER_CHANGE_MODE,
  ORDER_CHANGE_CURRENTPAGE,
  ORDER_SET_ORDER,
} from '../constants';

const rootRoute = '/api/order';

const getListingBaseUrl = (isReseller) => {
  return isReseller ? `${rootRoute}/reseller` : rootRoute;
};

const getOrderBaseUrl = (isReseller) => {
  return isReseller ? `${rootRoute}/resellerOrder` : rootRoute;
};

export const changeMode = (mode) => ({
  type: ORDER_CHANGE_MODE,
  payload: {
    mode,
  },
});

export const changeShowMyOrder = (showOnlyMyOrders) => ({
  type: ORDER_CHANGE_SHOW_MY_ORDERS,
  payload: {
    showOnlyMyOrders,
  },
});

/**
 *
 * @param {Object} params
 * @returns {string}
 */
const handleQueryParams = (params) => {
  const queryString = Object.keys(params)
    .map((key) =>
      params[key] !== null && params[key] !== undefined
        ? `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
        : ''
    )
    .filter(Boolean)
    .join('&');
  return queryString ? `?${queryString}` : '';
};

/**
 *
 * @param {Object} params
 * @param {number} [params.pageIndex=1]
 * @param {boolean} [params.showOnlyMyOrders=false]
 * @param {number} [params.pageSize=PaginationOptions.PageSize]
 * @param {string} [params.searchQuery=null]
 * @param {string} [params.statusFilter=null]
 * @param {Object} options
 * @param {string|null} [options.orderId=null]
 * @param {boolean} [options.showOrderDetail=false]
 * @param {boolean} [options.isReseller=false]
 * @returns
 */
export const query =
  (
    {
      pageIndex = 1,
      showOnlyMyOrders = false,
      pageSize = PaginationOptions.PageSize,
      searchQuery = null,
      statusFilter = null,
    },
    { orderId = null, showOrderDetail = false, isReseller = false }
  ) =>
  (dispatch) => {
    dispatch(setOrderStatus('idle'));
    return get(
      `${getListingBaseUrl(isReseller)}${handleQueryParams({
        pageIndex,
        showMyOrders: showOnlyMyOrders,
        pageSize,
        searchQuery,
        statusFilter,
      })}`
    )
      .then((response) => response.json())
      .then((result) => {
        dispatch(
          receive(
            result.orders,
            result.totalCount,
            result?.availableStatusFilters,
            pageIndex,
            showOnlyMyOrders,
            showOrderDetail ? ViewMode.Detail : ViewMode.List
          )
        );
        if (orderId && showOrderDetail) {
          const order = result.orders.find(
            (order) => order.orderId === orderId
          );
          dispatch(setOrder(order || {}));
        }
      })
      .catch((ex) => dispatch(catchError(ex, (error) => setError(error))));
  };

export const approveOrder = (orderId, callback) => (dispatch) => {
  return post(`${rootRoute}/approveOrder`, {
    id: orderId,
  })
    .then((response) => response.json())
    .then((result) => {
      callback && callback(result);
    })
    .catch((ex) => dispatch(catchError(ex, (error) => setError(error))));
};

/**
 *
 * @param {string} orderId
 * @param {boolean} isReseller
 * @returns
 */
export const getOrder =
  (orderId, isReseller = false) =>
  (dispatch) => {
    dispatch(setOrderStatus('idle'));
    return get(`${getOrderBaseUrl(isReseller)}/${orderId}`)
      .then((response) => response.json())
      .then((result) => {
        dispatch(receiveOrder(result));
      })
      .catch((ex) => dispatch(catchError(ex, (error) => setError(error))));
  };

export const approveResellerOrder = (orderId, callback) => (dispatch) => {
  dispatch(setOrderStatus('idle'));
  return post(
    `${rootRoute}/approveResellerOrder${handleQueryParams({
      id: orderId,
    })}`
  )
    .then((response) => response.json())
    .then((result) => {
      dispatch(setOrderStatus('success'));
      callback && callback(result);
    })
    .catch((ex) => dispatch(catchError(ex, (error) => setError(error))));
};

export const updateResellerOrder =
  (orderId, status, callback) => (dispatch) => {
    dispatch(setOrderStatus('idle'));
    return post(
      `${rootRoute}/updateResellerOrder${handleQueryParams({
        id: orderId,
        state: status,
      })}`
    )
      .then((response) => response.json())
      .then((result) => {
        dispatch(receiveOrder(result));
        dispatch(setOrderStatus('success'));
        callback && callback(result);
      })
      .catch((ex) => dispatch(catchError(ex, (error) => setError(error))));
  };

export const cancelResellerOrder = (orderId, callback) => (dispatch) => {
  dispatch(setOrderStatus('idle'));
  return post(
    `${rootRoute}/cancelResellerOrder${handleQueryParams({
      id: orderId,
    })}`
  )
    .then((response) => response.json())
    .then((result) => {
      dispatch(receiveOrder(result));
      dispatch(setOrderStatus('success'));
      callback && callback(result);
    })
    .catch((ex) => dispatch(catchError(ex, (error) => setError(error))));
};

export const sendResellerOrderReminder = (orderId, callback) => (dispatch) => {
  dispatch(setOrderStatus('idle'));
  console.log('Sending reseller order reminder for order ID:', orderId);
  return post(
    `${rootRoute}/sendResellerOrderReminder${handleQueryParams({
      id: orderId,
    })}`
  )
    .then((response) => response.json())
    .then((result) => {
      dispatch(receiveOrder(result));
      dispatch(setOrderStatus('success'));
      callback && callback(result);
    })
    .catch((ex) => dispatch(catchError(ex, (error) => setError(error))));
};

const receive = (
  list,
  totalCount,
  availableStatusFilters,
  currentPage,
  showOnlyMyOrders,
  mode = ViewMode.List,
  requestStatus = 'success'
) => ({
  type: ORDER_RECEIVE,
  payload: {
    list,
    mode,
    totalCount,
    currentPage,
    // showOnlyMyOrders,
    requestStatus,
    availableStatusFilters,
  },
});

const receiveOrder = (order, requestStatus = 'success') => ({
  type: ORDER_SET_ORDER,
  payload: {
    order,
    requestStatus,
    mode: ViewMode.Detail,
  },
});

export const setOrderStatus = (orderStatus) => ({
  type: ORDER_REQUEST_STATUS,
  payload: {
    requestStatus: orderStatus,
  },
});

export const changeCurrentPage = (currentPage) => ({
  type: ORDER_CHANGE_CURRENTPAGE,
  payload: {
    currentPage,
    requestStatus: 'idle',
  },
});

export const setError = (error) => ({
  type: ORDER_ERROR,
  payload: {
    error,
    requestStatus: 'error',
  },
});

export const setOrder = (order) => ({
  type: ORDER_SET_ORDER,
  payload: {
    order,
  },
});
